import type React from 'react'
import { NumberTicker } from '../NumberTicker'
import { DataChip } from './DataChip'

interface DataBarProps {
    value: number // 0 to 100 representing percentage width
    color?: 'green' | 'red' | 'neutral' // Defaults to neutral
    align?: 'left' | 'right'
    subText?: React.ReactNode
    showSpark?: boolean
}

export const DataBar: React.FC<DataBarProps> = ({
    value,
    color = 'neutral',
    align = 'right',
    subText,
    showSpark = true,
}) => {
    // Width Clamping
    const width = Math.min(Math.max(value, 0), 100)

    // Color Styles — lightweight soft gradient
    let beamGradient = 'from-slate-200 to-slate-150 dark:from-slate-700 dark:to-slate-750'

    if (color === 'green') {
        beamGradient =
            align === 'left'
                ? 'bg-linear-to-r from-emerald-400/80 via-emerald-300/70 to-emerald-200/60 dark:from-emerald-500/60 dark:via-emerald-600/50 dark:to-emerald-700/40'
                : 'bg-linear-to-l from-emerald-400/80 via-emerald-300/70 to-emerald-200/60 dark:from-emerald-500/60 dark:via-emerald-600/50 dark:to-emerald-700/40'
    } else if (color === 'red') {
        beamGradient =
            align === 'left'
                ? 'bg-linear-to-r from-rose-400/80 via-rose-300/70 to-rose-200/60 dark:from-rose-500/60 dark:via-rose-600/50 dark:to-rose-700/40'
                : 'bg-linear-to-l from-rose-400/80 via-rose-300/70 to-rose-200/60 dark:from-rose-500/60 dark:via-rose-600/50 dark:to-rose-700/40'
    }

    const isRight = align === 'right'

    return (
        <div className={`flex flex-col justify-center min-w-[60px] ${isRight ? 'items-end' : 'items-start'}`}>
            <div
                className={`h-[3px] w-full bg-slate-100/60 dark:bg-slate-800/30 rounded-full relative flex ${isRight ? 'justify-end' : 'justify-start'} overflow-hidden`}
            >
                <div
                    className={`h-full rounded-full relative transition-all duration-500 ease-out ${beamGradient}`}
                    style={{ width: `${width}%` }}
                />
            </div>
            {subText && <div className="mt-1">{subText}</div>}
        </div>
    )
}

// ==========================================
// Number Cell Component
// ==========================================

interface NumberCellProps {
    value: number | string
    isCurrency?: boolean
    decimals?: number
    showSign?: boolean
    className?: string // Additional classes
    trend?: 'up' | 'down' | 'neutral' // Explicit trend override
    suffix?: React.ReactNode
    fastPathId?: string
}

export const NumberCell: React.FC<NumberCellProps> = ({
    value,
    decimals = 2,
    showSign,
    className = '',
    trend,
    suffix,
    fastPathId,
}) => {
    let numVal = typeof value === 'number' ? value : parseFloat(value as string)
    if (Number.isNaN(numVal)) numVal = 0

    // Color Logic for DataChip
    let color: 'emerald' | 'rose' | 'slate' = 'slate'
    if (trend === 'up' || (!trend && numVal > 0)) color = 'emerald'
    if (trend === 'down' || (!trend && numVal < 0)) color = 'rose'
    if (trend === 'neutral' || numVal === 0) color = 'slate'

    // Override color based on className
    if (className.includes('text-emerald') || className.includes('text-green')) color = 'emerald'
    if (className.includes('text-rose') || className.includes('text-red')) color = 'rose'

    return (
        <DataChip 
            value={
                fastPathId ? (
                    <span data-fast-path={fastPathId}>
                        {typeof value === 'number' ? (showSign && numVal > 0 ? '+' : numVal < 0 ? '-' : '') + Math.abs(numVal).toFixed(decimals) : value}
                    </span>
                ) : typeof value === 'number' ? (
                    <NumberTicker
                        value={Math.abs(numVal)}
                        decimals={decimals}
                        prefix={showSign && numVal > 0 ? '+' : numVal < 0 ? '-' : ''}
                    />
                ) : (
                    value
                )
            }
            color={color}
            suffix={suffix}
            className={className}
        />
    )
}

// ==========================================
// Ghost Cell Wrapper (Layouts text above bar)
// ==========================================
interface GhostCellProps {
    val: number
    max: number
    fmt: string
    color: 'green' | 'red'
    align?: 'left' | 'right'
    showBar?: boolean
    subText?: React.ReactNode
    fastPathId?: string
    rankLabel?: string
}

export const GhostCell: React.FC<GhostCellProps> = ({
    val,
    max,
    fmt,
    color,
    align = 'right',
    showBar = true,
    subText,
    fastPathId,
    rankLabel,
}) => {
    const pct = max > 0 ? (Math.abs(val) / max) * 100 : 0

    const getRankLabelStyle = (rank: string) => {
        switch(rank) {
            case 'T1': return 'bg-emerald-400 text-emerald-950 border-emerald-500/50 dark:bg-emerald-500 dark:text-white dark:border-emerald-600 shadow-sm scale-[1.05]';
            case 'T2': return 'bg-emerald-200 text-emerald-800 border-emerald-300/80 dark:bg-emerald-900/80 dark:text-emerald-300 dark:border-emerald-700/80';
            case 'T3': return 'bg-emerald-100 text-emerald-600 border-emerald-200/60 dark:bg-emerald-900/40 dark:text-emerald-400 dark:border-emerald-800/60';
            case 'B1': return 'bg-rose-500 text-white border-rose-600/50 dark:bg-rose-600 dark:text-white dark:border-rose-700 shadow-sm scale-[1.05]';
            case 'B2': return 'bg-rose-200 text-rose-800 border-rose-300/80 dark:bg-rose-900/80 dark:text-rose-300 dark:border-rose-700/80';
            case 'B3': return 'bg-rose-100 text-rose-600 border-rose-200/60 dark:bg-rose-900/40 dark:text-rose-400 dark:border-rose-800/60';
            default: return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400';
        }
    }

    return (
        <div className={`relative flex flex-col justify-center h-full min-h-[36px] w-full ${align === 'right' ? 'items-end' : 'items-start'}`}>
            {rankLabel && (
                <div className={`absolute -top-1.5 ${align === 'right' ? 'right-0' : 'left-0'} z-10 flex justify-center`}>
                    <span className={`px-1 py-[1.5px] rounded-[3px] text-[7.5px] font-black leading-none whitespace-nowrap tracking-wider border shadow-xs ${getRankLabelStyle(rankLabel)} transition-transform`}>
                        {rankLabel}
                    </span>
                </div>
            )}
            <div className="flex items-center gap-1 mb-0.5 mt-2">
                {subText && align === 'right' && subText}
                <NumberCell value={fmt} fastPathId={fastPathId} className="text-[12px] font-semibold text-slate-600 dark:text-slate-400 relative z-0" />
                {subText && align === 'left' && subText}
            </div>
            {showBar && (
                <div className="w-full max-w-[70px]">
                    <DataBar value={pct} color={color} align={align} showSpark={false} />
                </div>
            )}
        </div>
    )
}
